﻿import {  EmailCustomRegistrationResend, EmailSpeakerResend, Registration, RegistrationAffiliation } from "@/api/dtos";
import ExtendedTableCell from "@/components/common/ExtendedTableCell";
import Loading from "@/components/common/Loading";
import { AdminRoutes } from "@/constants/routes";
import useSpeakers from "@/hooks/useSpeakers";
import { getFormRoute } from "@/utils/getComputedFormRoutes";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faBinoculars, faEye, faPaperPlane, faPlus, faSendBack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminNavigation from "../AdminNavigation";
import { faSend } from "@fortawesome/pro-duotone-svg-icons";
import { enqueueSnackbar } from "notistack";
import { FormsFeatureContext } from "@ngt/forms";
import { mutate } from "swr";

const Affiliations: Record<number, string> = {
    [RegistrationAffiliation.CAP]: "CAP",
    [RegistrationAffiliation.Board]: "Board Member",
    [RegistrationAffiliation.GuestSpeaker]: "Guest Speaker",
    [RegistrationAffiliation.TraineeAndEarlyCareersDayGrant]: "T&EC Day Grant",
    [RegistrationAffiliation.EducationTravelGrant]: "Education Travel Grant",
    [RegistrationAffiliation.Other]: "Other"
}

const AdminSpeakerHome: React.FunctionComponent = () => {

    const location = useLocation();

    const navigate = useNavigate();

    const theme = useTheme();

    const { registrations, loading } = useSpeakers();
        

    const processedRef = useRef(false); // this is to prevent multiple snackbars on re-renders
    //This is used to queue the snackbar while also refreshing the page to get new registrations
    useEffect(() => {
        if (location.state?.successMessage && !processedRef.current) {
            
            processedRef.current = true;

            if (mutate) {
                mutate(() => true, undefined, { revalidate: true }); 
            }

            enqueueSnackbar(location.state.successMessage, { variant: 'success' });

            window.history.replaceState({}, document.title);
        }
    }, [location.state, mutate]);

    const handleNewClick = () => {
        navigate(AdminRoutes.ADMIN_ADD_SPEAKER);
    }

    const handleSpeakerClick = (id: number) => {
        navigate(getFormRoute(AdminRoutes.ADMIN_SPEAKER_BY_ID, id.toString()))
    }

    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const [affiliationFilter, setAffiliationFilter] = useState<string>('All');

    const sortedRegistrations = React.useMemo(() => {
        if (!registrations) return [];

        let filtered = [...registrations];

        if (affiliationFilter !== 'All') {
            
            filtered = filtered.filter(reg => {
                switch (affiliationFilter) {
                    case 'Speaker':
                        return reg.registrationAffiliation == RegistrationAffiliation.GuestSpeaker; 
                    case 'Board':
                        return reg.registrationAffiliation == RegistrationAffiliation.Board;
                    case 'CAP':
                        return reg.registrationAffiliation == RegistrationAffiliation.CAP;
                    case 'EducationTravelGrant':
                        return reg.registrationAffiliation == RegistrationAffiliation.EducationTravelGrant;
                    case 'TraineeAndEarlyCareerTravelGrant':
                        return reg.registrationAffiliation == RegistrationAffiliation.TraineeAndEarlyCareersDayGrant;
                    case 'Other':
                        return reg.registrationAffiliation == RegistrationAffiliation.Other;
                        
                    default:
                        return true;
                }
            });
        }

        let sorted = [...filtered];
        if (sortConfig !== null) {
            sorted.sort((a, b) => {
                switch (sortConfig.key) {
                    case 'firstName':
                        if (!a.firstName && !b.firstName) {
                            return 0;
                        }
                        if (!a.firstName) {
                            return -1;
                        }
                        if (!b.firstName) {
                            return 1;
                        }
                        return sortConfig.direction === 'asc'
                            ? a.firstName.localeCompare(b.firstName)
                            : b.firstName.localeCompare(a.firstName);
                    case 'lastName':
                        if (!a.lastName && !b.lastName) {
                            return 0;
                        }
                        if (!a.lastName) {
                            return -1;
                        }
                        if (!b.lastName) {
                            return 1;
                        }
                        return sortConfig.direction === 'asc'
                            ? a.lastName.localeCompare(b.lastName)
                            : b.lastName.localeCompare(a.lastName);
                    case 'institution':
                        if (!a.institution && !b.institution) {
                            return 0;
                        }
                        if (!a.institution) {
                            return -1;
                        }
                        if (!b.institution) {
                            return 1;
                        }
                        return sortConfig.direction === 'asc'
                            ? a.institution.localeCompare(b.institution)
                            : b.institution.localeCompare(a.institution);
                    case 'affiliation':
                        const aAff = a.registrationAffiliation ? Affiliations[a.registrationAffiliation] || '' : '';
                        const bAff = b.registrationAffiliation ? Affiliations[b.registrationAffiliation] || '' : '';
                        return sortConfig.direction === 'asc'
                            ? aAff.localeCompare(bAff)
                            : bAff.localeCompare(aAff);
                    case 'enteredDate':
                        if (!a.enteredDate && !b.enteredDate) {
                            return 0;
                        }
                        if (!a.enteredDate) {
                            return -1;
                        }
                        if (!b.enteredDate) {
                            return 1;
                        }
                        return sortConfig.direction === 'asc'
                            ? new Date(a.enteredDate).getTime() - new Date(b.enteredDate).getTime()
                            : new Date(b.enteredDate).getTime() - new Date(a.enteredDate).getTime();
                    default:
                        return 0;
                }
            });
        }
        return sorted;
    }, [registrations, sortConfig, affiliationFilter]);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const {serviceStackClient} = useContext(FormsFeatureContext);
    
    const [openWarning, setOpenWarning] = useState(false);

    const [registrationId, setRegistrationId] = useState<number | undefined>(undefined);
    const [isSpeaker, setIsSpeaker] = useState<boolean>(true);

    const handleClickOpen = () => {
        setOpenWarning(true);
    };

    const handleEmailConfirm = () => {
        setOpenWarning(false);
        onClickResendEmail();
    }
    
    const handleClose = () => {
        setOpenWarning(false);
    };
    
    const onClickResendEmail = async () => {

        if (!registrationId) {
            return
        }
        try{
            if(isSpeaker){
                const response = await serviceStackClient.post(new EmailSpeakerResend({
                    registrationId: registrationId
                }));
            }
            else{
                  const response = await serviceStackClient.post(new EmailCustomRegistrationResend({
                    registrationId: registrationId
                }));
            }
            enqueueSnackbar('Resend email successful', {variant: 'success'});
        }
        catch(err: any){
            enqueueSnackbar("Error while tying to resend email", {variant: 'error'});
        }
    }

    return (
        <>
                        
            <Dialog
                open={openWarning}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Are you sure?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to resend the email?
                    </DialogContentText>
                </DialogContent>
                <DialogActions
                    sx={{
                        paddingX: "24px",
                        paddingBottom: "16px",
                    }}
                >
                    <Button 
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleEmailConfirm}
                        autoFocus
                    >
                        Resend Email
                    </Button>
                </DialogActions>
            </Dialog>


            <Box>
                <AdminNavigation />
                <Box>
                    <Container
                        maxWidth={false}
                    >
                        <Box
                            sx={{
                                px: {
                                    xs: 1
                                }
                            }}
                        >
                            <Grid
                                container
                                justifyContent="space-between"
                                sx={{
                                    mb: {
                                        xs: 2
                                    },
                                    mt: {
                                        xs: 5
                                    }
                                }}
                            >
                                <Grid
                                    item
                                    xs={6}
                                >
                                    <Typography
                                        variant="h2"
                                        component="h1"
                                        gutterBottom
                                    >
                                        Custom Registrations
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'end',
                                        gap: 2
                                    }}
                                >
                                <FormControl size="small" variant="outlined" sx={{ minWidth: 250 }}>
                                    <InputLabel id="affiliation-select-label">Filter by Affiliate Type</InputLabel>
                                    <Select
                                        sx={{ minWidth: 250 }}
                                        labelId="affiliation-select-label"
                                        id="affiliation-select"
                                        value={affiliationFilter}
                                        label="Filter by Affiliate Type"
                                        onChange={(e) => setAffiliationFilter(e.target.value)}
                                    >
                                        <MenuItem value="All">
                                            All Affiliations
                                        </MenuItem>
                                        <MenuItem value="Speaker">
                                            Speaker
                                        </MenuItem>
                                        <MenuItem value="Board">
                                            Board
                                        </MenuItem>
                                        <MenuItem value="SAC">
                                            SAC
                                        </MenuItem>
                                        <MenuItem value="CAP">
                                            CAP
                                        </MenuItem>
                                        <MenuItem value="TraineeAndEarlyCareerTravelGrant">
                                            Trainee and early career Travel Grant
                                        </MenuItem>
                                        <MenuItem value="EducationTravelGrant">
                                            Education Travel Grant
                                        </MenuItem>
                                        <MenuItem value="Other">
                                            Other
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                    <Tooltip placement="right" title="New Custom Registration">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNewClick}
                                            sx={{
                                                p: {
                                                    xs: 1
                                                },
                                                minWidth: 48,
                                                maxHeight: 48,
                                                height: '100%',
                                                fontSize: '16px',
                                                borderColor: 'primary.main',
                                                ":hover": {
                                                    borderColor: 'secondary.main',
                                                    backgroundColor: 'secondary.main',
                                                    color: '#FFF'
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} fixedWidth />
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            {
                                !loading && (!registrations || registrations.length === 0) ?
                                    <Alert
                                        severity="info"
                                        icon={<FontAwesomeIcon fixedWidth icon={faExclamationCircle} />}
                                    >
                                        There are no Custom Registrations
                                    </Alert>
                                    :
                                    <TableContainer component={Paper}>
                                        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    <ExtendedTableCell
                                                        handleClick={() => handleSort('firstName')}
                                                        sorted={
                                                            sortConfig?.key === 'firstName' && sortConfig.direction === 'asc' ||
                                                            sortConfig?.key === 'firstName' && sortConfig.direction === 'desc'
                                                        }
                                                        sortDescending={sortConfig?.key === 'firstName' && sortConfig.direction === 'desc'}
                                                    >
                                                        First Name
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                        handleClick={() => handleSort('lastName')}
                                                        sorted={
                                                            sortConfig?.key === 'lastName' && sortConfig.direction === 'asc' ||
                                                            sortConfig?.key === 'lastName' && sortConfig.direction === 'desc'
                                                        }
                                                        sortDescending={sortConfig?.key === 'lastName' && sortConfig.direction === 'desc'}
                                                    >
                                                        Last Name
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                        handleClick={() => handleSort('institution')}
                                                        sorted={
                                                            sortConfig?.key === 'institution' && sortConfig.direction === 'asc' || 
                                                            sortConfig?.key === 'institution' && sortConfig.direction === 'desc'
                                                        }
                                                        sortDescending={sortConfig?.key === 'institution' && sortConfig.direction === 'desc'}
                                                    >
                                                        Institution
                                                    </ExtendedTableCell>

                                                
                                                    <ExtendedTableCell
                                                        handleClick={() => handleSort('affiliation')}
                                                        sorted={
                                                            sortConfig?.key === 'affiliation' && sortConfig.direction === 'asc' || 
                                                            sortConfig?.key === 'affiliation' && sortConfig.direction === 'desc'
                                                        }
                                                        sortDescending={sortConfig?.key === 'affiliation' && sortConfig.direction === 'desc'}
                                                    >
                                                        Affiliation
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                        handleClick={() => handleSort('enteredDate')}
                                                        sorted={
                                                            sortConfig?.key === 'enteredDate' && sortConfig.direction === 'asc' ||
                                                            sortConfig?.key === 'enteredDate' && sortConfig.direction === 'desc'
                                                        }
                                                        sortDescending={sortConfig?.key === 'enteredDate' && sortConfig.direction === 'desc'}
                                                    >
                                                        Date Added
                                                    </ExtendedTableCell>
                                                    <ExtendedTableCell
                                                        justify="center"
                                                    >
                                                        Resend Email
                                                    </ExtendedTableCell>
                                                    <ExtendedTableCell
                                                        justify="center"
                                                    >
                                                        Affiliates View
                                                    </ExtendedTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    loading ?
                                                        <Loading />
                                                        :
                                                        sortedRegistrations.map((registration) => (
                                                            <TableRow
                                                                key={`speaker-${registration.id}`}
                                                                sx={{
                                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                                    // ":hover": {
                                                                    //     cursor: 'pointer',
                                                                    // },
                                                                }}
                                                                //onClick={() => handleSpeakerClick(registration.id!)}
                                                            >
                                                                <TableCell>
                                                                    {registration.firstName}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {registration.lastName}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {registration.institution}
                                                                </TableCell>
                                                                  <TableCell>
                                                                    {registration.registrationAffiliation ? Affiliations[registration.registrationAffiliation] : ""}
                                                                </TableCell>
                                                           
                                                                <TableCell>
                                                                    {registration.enteredDate && new Date(registration.enteredDate).toLocaleDateString('en-GB')}
                                                                </TableCell>
                                                                <Tooltip placement="right" title="Resend Email to affiliate that contains the link to their form">
                                                                    <TableCell
                                                                        onClick={() => {
                                                                            setRegistrationId(registration.id);
                                                                            registration.isSpeaker ? setIsSpeaker(true) : setIsSpeaker(false);
                                                                            handleClickOpen();
                                                                        }}
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                            color: theme.palette.primary.main,
                                                                            cursor: 'pointer',
                                                                            '&:hover':{
                                                                                color: theme.palette.secondary.main,
                                                                                backgroundColor: theme.palette.grey[300]  
                                                                            }
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon 
                                                                            icon={faPaperPlane} 
                                                                            fixedWidth 
                                                                            color="secondary" 
                                                                            size="xl"
                                                                        />
                                                                    </TableCell>
                                                                </Tooltip>
                                                                <Tooltip placement="right" title="View the form as the affiliate would see it (navigate to their link)">
                                                                    <TableCell
                                                                        onClick={() => {
                                                                            if(registration.isSpeaker){
                                                                                window.open("/speaker/" + registration.guid, "_blank");
                                                                            }
                                                                            else{
                                                                                window.open("/registration-update/" + registration.guid, "_blank");
                                                                            }


                                                                        }}
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                            cursor: 'pointer',
                                                                            color: theme.palette.primary.main,
                                                                            '&:hover':{
                                                                                color: theme.palette.secondary.main,
                                                                                backgroundColor: theme.palette.grey[300]  
                                                                            }
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon 
                                                                            icon={faBinoculars} 
                                                                            fixedWidth 
                                                                            size="xl"
                                                                        />
                                                                    </TableCell>
                                                                </Tooltip>
                                                            </TableRow>
                                                        ))
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                            }
                        </Box>
                    </Container>
                </Box>
            </Box>
        </>
    )
}

export default AdminSpeakerHome;